import logError from '@/helpers/logError';
import { ImageFile } from '../../../state';
import { measureStringDifference } from './measureStringDifference';
import { HubspotFile } from '@/helpers/hubspot/types';

/*******************************************************************************
 * imageMatcherAI
 * This function takes a list of images and a list of responses from the server.
 * It then tries to match the images to the responses by comparing the file names
 * of the images to the names of the responses. If the file names are similar
 * enough, the function will assume that the file is the same as the response.
 */
type ReducerAIShape = { score: number; response: HubspotFile | null };
export function imageMatcherAI(
	images: ImageFile[],
	responses: HubspotFile[],
): ImageFile[] {
	try {
		let remainingResponses = [...responses];
		const updatedImages = images.map((image) => {
			const matched = remainingResponses.reduce(
				(acc, res) => {
					const score = measureStringDifference(
						image.image?.name ?? '',
						`${res.name}.${res.extension}`,
					);
					if (score < acc.score) {
						acc.score = score;
						acc.response = res;
					}
					return acc;
				},
				{
					score: Number.MAX_SAFE_INTEGER,
					response: null,
				} as ReducerAIShape,
			);

			if (!matched.response) {
				return { ...image, error: 'Failed to match image' };
			}

			remainingResponses = remainingResponses.filter(
				(response) => response.name !== matched.response?.name,
			);
			const updatedImage: ImageFile = {
				...image,
				id: matched.response.id,
				error: null,
			};
			return updatedImage;
		});
		return updatedImages;
	} catch (error) {
		if (error instanceof Error) {
			logError(error);
		} else {
			logError(new Error('Failed to match images'));
		}
		return [];
	}
}
